import { Banner, ProgressBar, Toast, Tooltip } from "@shopify/polaris"
import { useContext, useEffect, useState } from "react"
import { useFirestoreDataUpdate } from "../../hooks/useFirestoreDataUpdate"
import { useFirestoreImportHistoryUpdate } from "../../hooks/useFirestoreImportHistoryUpdate.js"
import { useImportProduct } from "../../hooks/useImportProduct.js"
import { pushCrispEvent } from "../../utils/crisp.js"
import { GlobalStateContext } from "../providers/GlobalStateProvider"
import { useTranslation } from "react-i18next"
import { usePrepareAndImportProduct } from "../../hooks/usePrepareAndImportProduct.js"

export default function UploadSingleProduct(props) {
	const { state, dispatch } = useContext(GlobalStateContext)
	const { hasImportedAProduct } = state
	const { t } = useTranslation()

	const [uploading, setUploading] = useState(false)
	const [finishedUploading, setFinishedUploading] = useState(false)

	const [showToast, setShowToast] = useState(true)
	const [showError, setShowError] = useState(false)
	const [reviewModalIsOpen, setReviewModalIsOpen] = useState(false)
	const [uploadError, setUploadError] = useState("")
	const [step, setStep] = useState(0)

	const [productPreviewUrl, setProductPreviewUrl] = useState("")
	const [productEditUrl, setProductEditUrl] = useState("")

	const updateFirestoreUserData = useFirestoreDataUpdate()
	const updateImportHistory = useFirestoreImportHistoryUpdate()
	const prepareAndImportProduct = usePrepareAndImportProduct()

	const uploadProduct = async () => {
		setUploading(true)
		setShowToast(true)
		setFinishedUploading(false)
		setShowError(false)

		try {
			const { data, errors } = await prepareAndImportProduct(
				props.input,
				props.productUrl,
			)

			if (errors?.length) {
				console.error("UploadSingleProduct - errors", errors)
				setShowError(true)
				if (
					errors.some((err) =>
						(
							err?.graphQLErrors?.[0]?.message ||
							err?.message ||
							""
						).includes("not supported on trial accounts"),
					)
				) {
					setUploadError(
						'Uploading images is not supported on stores with Shopify trial plans. Please upgrade your Shopify store plan to at least "Basic" and try importing the product again.',
					)
				} else {
					setUploadError(
						errors
							.map((err, i) => {
								const errMessage =
									err.graphQLErrors?.[0]?.message ||
									err.message ||
									"Unspecified error 3"
								return `${i + 1}. ${errMessage}`
							})
							.join("\n"),
					)
				}
				props.onFailedUploading()
			} else {
				if (!hasImportedAProduct) {
					updateFirestoreUserData({ hasImportedAProduct: true })
				}
				updateImportHistory({
					productTitle: props.input.product.title || "",
					productUrl: props.productUrl || "",
					productImageUrl: props.input.product.images[0]?.src || "",
					dateImported: new Date(),
					importMode: "single",
					productId: data?.productCreate?.product?.id || "",
				})
				pushCrispEvent("single_product_import_successful", {
					productUrl: props.productUrl,
				})

				if (
					data?.productCreate?.product?.onlineStorePreviewUrl &&
					!data.productCreate.product.onlineStorePreviewUrl.includes(
						"shopifypreview.com",
					)
				) {
					setProductPreviewUrl(
						data.productCreate.product.onlineStorePreviewUrl,
					)
				} else if (data?.productCreate?.product?.id) {
					const id =
						/\d+/.exec(data.productCreate.product.id)?.[0] || null
					if (id && state.shop) {
						const store =
							state.shop.split(".myshopify.com")?.[0] || null
						if (store) {
							setProductEditUrl(
								`https://admin.shopify.com/store/${store}/products/${id}`,
							)
						}
					}
				}
			}

			setUploading(false)
			setFinishedUploading(true)
			props.onFinishedUploading(data?.productCreate?.product?.id || "")
		} catch (err) {
			console.error("UploadSingleProduct - catched error", err)
			setShowError(true)
			setUploadError(JSON.stringify(err, Object.getOwnPropertyNames(err)))
			props.onFailedUploading()
		}
	}

	useEffect(() => {
		if (!uploading && props.readyToUpload && props.input.product) {
			uploadProduct()
		}
	}, [props.readyToUpload, props.input])

	if (uploading) {
		return (
			<div
				style={{
					marginTop: "1rem",
					display: "flex",
					justifyContent: "center",
				}}
			>
				{uploading ? (
					<div style={{ maxWidth: "60rem", width: "100%" }}>
						<ProgressBar size="small" progress={(step / 3) * 100} />
						<p style={{ textAlign: "right" }}>
							{step === 0 ? (
								t("single.creating_product")
							) : step === 1 ? (
								<Tooltip
									content={
										<div
											style={{ padding: "0.4rem 0.7rem" }}
										>
											{t(
												"single.this_might_take_a_while_if_the_product_contains_a_lot_of_images",
											)}
										</div>
									}
								>
									<span
										style={{
											textDecoration:
												"underline dotted #777",
										}}
									>
										{t("single.uploading_product_images")}
									</span>
								</Tooltip>
							) : step === 2 ? (
								t(
									"single.updating_product_with_uploaded_images",
								)
							) : (
								t("single.uploading")
							)}
						</p>
					</div>
				) : (
					""
				)}
			</div>
		)
	} else if (finishedUploading && showError) {
		return (
			<div style={{ marginTop: "2rem" }}>
				<Banner
					title={t("single.error_importing_product")}
					action={{
						content: t("single.retry"),
						onAction: uploadProduct,
					}}
					status="critical"
				>
					<p>
						{t(
							"single.an_error_ocurred_when_importing_the_product",
						)}
					</p>
					<p style={{ fontFamily: "monospace" }}>{uploadError}</p>
				</Banner>
			</div>
		)
		// } else if (finishedUploading && showToast && !hasImportedAProduct && reviewModalIsOpen) {
		// 	return (
		// 		<Modal open={reviewModalIsOpen} onClose={() => setReviewModalIsOpen(false)} title="Product successfully imported!" primaryAction={{
		// 			content: "Leave a review",
		// 			onAction: () => window.open("https://apps.shopify.com/kopy/reviews", "_blank")
		// 		}}>
		// 			<Modal.Section>
		// 				<Heading>Congrats on importing your first product!</Heading>
		// 				<p>Like the app? Consider leaving us a review.</p>
		// 			</Modal.Section>
		// 		</Modal>
		// 	)
	} else if (finishedUploading && showToast) {
		return (
			<Toast
				content={t("single.product_imported")}
				onDismiss={() => setShowToast(false)}
				duration={86400000}
				action={
					productPreviewUrl || productEditUrl
						? {
								content: t("single.view_product"),
								onAction: () =>
									window
										.open(
											productEditUrl || productPreviewUrl,
											"_blank",
										)
										?.focus(),
							}
						: null
				}
			/>
		)
	}

	return ""
}
